import { useSyncExternalStore } from "react";

/**
 * Custom breakpoints for mobile detection
 * - MOBILE_BREAKPOINT: 768px (md) - primary mobile/desktop boundary
 * - SMALL_MOBILE_BREAKPOINT: 640px (sm) - for additional small screen adjustments
 */
const MOBILE_BREAKPOINT = 768;
const SMALL_MOBILE_BREAKPOINT = 640;
const DESKTOP_BREAKPOINT = 1024;

type BreakpointSnapshot = {
  isMobile: boolean;
  isSmallMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;
};

const SERVER_SNAPSHOT: BreakpointSnapshot = {
  isMobile: false,
  isSmallMobile: false,
  isTablet: false,
  isDesktop: true,
};

let currentSnapshot = SERVER_SNAPSHOT;
let mediaQueries: MediaQueryList[] | null = null;
let viewportListener: (() => void) | null = null;
let usingResizeFallback = false;

const subscribers = new Set<() => void>();

function computeSnapshot(): BreakpointSnapshot {
  if (typeof window === "undefined") return SERVER_SNAPSHOT;

  const width = window.innerWidth;
  return {
    isMobile: width < MOBILE_BREAKPOINT,
    isSmallMobile: width < SMALL_MOBILE_BREAKPOINT,
    isTablet: width >= MOBILE_BREAKPOINT && width < DESKTOP_BREAKPOINT,
    isDesktop: width >= DESKTOP_BREAKPOINT,
  };
}

function isSameSnapshot(a: BreakpointSnapshot, b: BreakpointSnapshot): boolean {
  return (
    a.isMobile === b.isMobile &&
    a.isSmallMobile === b.isSmallMobile &&
    a.isTablet === b.isTablet &&
    a.isDesktop === b.isDesktop
  );
}

function readSnapshot(): BreakpointSnapshot {
  const next = computeSnapshot();
  if (isSameSnapshot(currentSnapshot, next)) return currentSnapshot;
  currentSnapshot = next;
  return currentSnapshot;
}

function notifyIfChanged() {
  const next = computeSnapshot();
  if (isSameSnapshot(currentSnapshot, next)) return;

  currentSnapshot = next;
  subscribers.forEach((listener) => listener());
}

function subscribeToViewport(listener: () => void) {
  subscribers.add(listener);
  readSnapshot();

  if (typeof window === "undefined") {
    return () => {
      subscribers.delete(listener);
    };
  }

  if (subscribers.size === 1) {
    viewportListener = notifyIfChanged;
    if (typeof window.matchMedia === "function") {
      mediaQueries = [
        window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`),
        window.matchMedia(`(max-width: ${SMALL_MOBILE_BREAKPOINT - 1}px)`),
        window.matchMedia(`(min-width: ${DESKTOP_BREAKPOINT}px)`),
      ];
      mediaQueries.forEach((query) => query.addEventListener("change", viewportListener!));
    } else {
      usingResizeFallback = true;
      window.addEventListener("resize", viewportListener);
    }
  }

  return () => {
    subscribers.delete(listener);
    if (subscribers.size > 0 || !viewportListener) return;

    if (mediaQueries) {
      mediaQueries.forEach((query) => query.removeEventListener("change", viewportListener!));
    }
    if (usingResizeFallback) {
      window.removeEventListener("resize", viewportListener);
    }

    mediaQueries = null;
    viewportListener = null;
    usingResizeFallback = false;
  };
}

/**
 * Hook to detect if the current viewport is mobile-sized
 * Uses window.matchMedia for performance and reactivity
 *
 * @returns {boolean} true if viewport width < 768px
 */
export function useIsMobile(): boolean {
  return useBreakpoints().isMobile;
}

/**
 * Hook to detect if the current viewport is small mobile (< 640px)
 * Useful for extra-compact layouts on very small phones
 *
 * @returns {boolean} true if viewport width < 640px
 */
export function useIsSmallMobile(): boolean {
  return useBreakpoints().isSmallMobile;
}

/**
 * Hook that provides multiple breakpoint states
 * Useful when you need to check multiple breakpoints at once
 * Uses matchMedia for performance and to avoid cascading renders
 */
export function useBreakpoints() {
  return useSyncExternalStore(subscribeToViewport, readSnapshot, () => SERVER_SNAPSHOT);
}
